import { TaskItem, TeamMember } from '@demind-inc/core';
import dayjs from 'dayjs';

import { TeamTaskBucket, TeamTaskItem } from '../../../data-access';
import { getDueDateYYYYMMDD } from '../../../helpers';
import {
  getTeamMemberDisplayName,
  getTeamMemberInitials,
  getTeamMemberPhotoUrl,
} from './teamDayUtils';

export type TeamTasksFilter = 'all' | 'mine' | TeamTaskBucket;
export type TeamTasksGroupBy = 'due' | 'member' | 'board';
type TeamTFunction = (
  key: string,
  options?: Record<string, unknown> & { defaultValue?: string }
) => string;

export interface TeamTaskOwner {
  id: string;
  name: string;
  initials: string;
  photoUrl: string;
  role?: TeamMember['role'];
  isCurrentUser: boolean;
}

export type TeamTaskWithOwner = Omit<TeamTaskItem, 'owner' | 'assignee'> & {
  owner: TeamTaskOwner;
  assignee?: TeamTaskOwner;
};

export interface TeamTaskGroup {
  key: string;
  label: string;
  bucket?: TeamTaskBucket;
  owner?: TeamTaskOwner;
  provider?: string;
  items: TeamTaskWithOwner[];
}

export const TEAM_TASK_BUCKET_ORDER: TeamTaskBucket[] = ['overdue', 'today', 'upcoming', 'done'];

const TODO_PROVIDER_ICON_KEYS = [
  'todoist',
  'trello',
  'ticktick',
  'clickup',
  'github',
  'google-tasks',
  'lifestack',
  'apple-reminders',
  'notion',
] as const;

export type TodoProviderIconKey = (typeof TODO_PROVIDER_ICON_KEYS)[number];

export const normalizeTeamTaskProviderIconKey = (appFrom?: string): TodoProviderIconKey | null => {
  if (!appFrom) {
    return null;
  }

  const normalizedProvider =
    appFrom === 'googleTasks'
      ? 'google-tasks'
      : appFrom === 'appleReminders'
        ? 'apple-reminders'
        : appFrom;

  return TODO_PROVIDER_ICON_KEYS.includes(normalizedProvider as TodoProviderIconKey)
    ? (normalizedProvider as TodoProviderIconKey)
    : null;
};

export const isTeamBoardTask = (taskItem: Pick<TeamTaskItem, 'source'>) => {
  return taskItem.source === 'team-board';
};

export const getTeamTaskAssigneeUserId = (taskItem: TeamTaskItem | TeamTaskWithOwner) => {
  const assignee = taskItem.assignee as { userId?: string; id?: string } | undefined;

  return assignee?.userId || assignee?.id || '';
};

const buildTeamTaskDisplayOwner = (
  member: TeamTaskItem['owner'] | NonNullable<TeamTaskItem['assignee']>,
  fallbackName: string,
  currentUserId: string,
  t?: TeamTFunction
): TeamTaskOwner => {
  const name =
    member.userId === currentUserId
      ? t
        ? t('team.common.youName', { name: fallbackName })
        : `${fallbackName} (You)`
      : fallbackName;

  return {
    id: member.userId || member.teamMemberId || fallbackName,
    name,
    initials: getTeamMemberInitials(fallbackName),
    photoUrl: member.photoUrl || '',
    role: member.role,
    isCurrentUser: member.userId === currentUserId,
  };
};

export const mapTeamTasksForDisplay = (
  tasks: TeamTaskItem[],
  currentUserId: string,
  t?: TeamTFunction
): TeamTaskWithOwner[] => {
  return tasks.map((taskItem, index) => {
    const ownerName =
      taskItem.owner.name?.trim() ||
      (t ? t('team.common.memberWithIndex', { index: index + 1 }) : `Member ${index + 1}`);
    const assigneeName = taskItem.assignee?.name?.trim() || (t ? t('team.common.unassigned') : '');

    return {
      ...taskItem,
      owner: buildTeamTaskDisplayOwner(taskItem.owner, ownerName, currentUserId, t),
      assignee: taskItem.assignee
        ? buildTeamTaskDisplayOwner(taskItem.assignee, assigneeName, currentUserId, t)
        : undefined,
    };
  });
};

export const getTeamTaskForDisplay = (
  taskItem: TeamTaskItem | TeamTaskWithOwner,
  t?: TeamTFunction
) => {
  const task = taskItem.task as TaskItem & { title?: string };

  return {
    ...task,
    name: task.name?.trim() || task.title?.trim() || (t ? t('team.tasks.untitledTask') : 'task'),
    boardId: task.boardId || taskItem.project.boardId,
    appFrom: task.appFrom || taskItem.project.appFrom || 'lifestack',
  } as TaskItem;
};

export const getTeamTaskKey = (taskItem: TeamTaskWithOwner, index: number) => {
  const task = taskItem.task as TaskItem & { title?: string };

  return [
    taskItem.owner.id,
    taskItem.project.boardId,
    task.taskId || task.originalTaskId || task.name || task.title || 'task',
    task.dueDateTime?.datetime || task.dueDateTime?.date || index,
    index,
  ].join('-');
};

export const getTeamTaskDueLabel = (
  task: TaskItem,
  bucket: TeamTaskBucket,
  t?: TeamTFunction,
  language?: string
) => {
  if (task.dueDateTime?.datetime && bucket !== 'done') {
    return dayjs(task.dueDateTime.datetime).format('h:mm A');
  }

  const dueDate = getDueDateYYYYMMDD(task.dueDateTime);
  if (dueDate && (bucket === 'upcoming' || bucket === 'overdue')) {
    if (language) {
      return new Intl.DateTimeFormat(language, { month: 'short', day: 'numeric' }).format(
        dayjs(dueDate).toDate()
      );
    }

    return dayjs(dueDate).format('MMM D');
  }

  if (!dueDate && bucket !== 'done') {
    return t ? t('team.tasks.noDueDate') : '';
  }

  return t ? t(`team.tasks.buckets.${bucket}`) : bucket;
};

const getTeamTaskDueValue = (taskItem: TeamTaskWithOwner) => {
  const task = getTeamTaskForDisplay(taskItem);

  if (task.dueDateTime?.datetime) {
    return dayjs(task.dueDateTime.datetime).valueOf();
  }

  const dueDate = getDueDateYYYYMMDD(task.dueDateTime);
  return dueDate ? dayjs(dueDate).startOf('day').valueOf() : Number.MAX_SAFE_INTEGER;
};

const compareTeamTaskDueValue = (left: TeamTaskWithOwner, right: TeamTaskWithOwner) => {
  const leftDueValue = getTeamTaskDueValue(left);
  const rightDueValue = getTeamTaskDueValue(right);

  if (leftDueValue === Number.MAX_SAFE_INTEGER && rightDueValue !== Number.MAX_SAFE_INTEGER) {
    return 1;
  }

  if (rightDueValue === Number.MAX_SAFE_INTEGER && leftDueValue !== Number.MAX_SAFE_INTEGER) {
    return -1;
  }

  if (leftDueValue === rightDueValue) {
    return 0;
  }

  if (left.bucket === 'overdue' && right.bucket === 'overdue') {
    return rightDueValue - leftDueValue;
  }

  if (left.bucket === 'done' && right.bucket === 'done') {
    return rightDueValue - leftDueValue;
  }

  return leftDueValue - rightDueValue;
};

export const sortTeamTasks = (tasks: TeamTaskWithOwner[]) => {
  return [...tasks].sort((left, right) => {
    const leftTask = getTeamTaskForDisplay(left);
    const rightTask = getTeamTaskForDisplay(right);

    if (!!leftTask.completed !== !!rightTask.completed) {
      return leftTask.completed ? 1 : -1;
    }

    const leftBucketIndex = TEAM_TASK_BUCKET_ORDER.indexOf(left.bucket);
    const rightBucketIndex = TEAM_TASK_BUCKET_ORDER.indexOf(right.bucket);
    if (leftBucketIndex !== rightBucketIndex) {
      return leftBucketIndex - rightBucketIndex;
    }

    const dueComparison = compareTeamTaskDueValue(left, right);
    if (dueComparison !== 0) {
      return dueComparison;
    }

    const leftSortIndex = leftTask.sortIndex ?? Number.MAX_SAFE_INTEGER;
    const rightSortIndex = rightTask.sortIndex ?? Number.MAX_SAFE_INTEGER;
    if (leftSortIndex !== rightSortIndex) {
      return leftSortIndex - rightSortIndex;
    }

    return leftTask.name.localeCompare(rightTask.name);
  });
};

export const filterTeamTasks = (tasks: TeamTaskWithOwner[], filter: TeamTasksFilter) => {
  if (filter === 'all') {
    return sortTeamTasks(tasks.filter((taskItem) => taskItem.bucket !== 'done'));
  }

  if (filter === 'mine') {
    return sortTeamTasks(tasks.filter((taskItem) => taskItem.isMeAssigned));
  }

  return sortTeamTasks(tasks.filter((taskItem) => taskItem.bucket === filter));
};

export const buildTeamTaskGroups = (
  tasks: TeamTaskWithOwner[],
  groupBy: TeamTasksGroupBy,
  t?: TeamTFunction
) => {
  const sortedTasks = sortTeamTasks(tasks);

  if (groupBy === 'due') {
    return TEAM_TASK_BUCKET_ORDER.map((bucket) => ({
      key: bucket,
      label: t ? t(`team.tasks.buckets.${bucket}`) : bucket,
      bucket,
      items: sortedTasks.filter((taskItem) => taskItem.bucket === bucket),
    })).filter((group) => group.items.length > 0);
  }

  if (groupBy === 'member') {
    const groupsByOwner: Record<string, TeamTaskGroup> = {};

    sortedTasks.forEach((taskItem) => {
      const member = isTeamBoardTask(taskItem) ? taskItem.assignee : taskItem.owner;
      const ownerId = member?.id || 'unassigned';

      if (!groupsByOwner[ownerId]) {
        groupsByOwner[ownerId] = {
          key: ownerId,
          label: member
            ? member.isCurrentUser
              ? t
                ? t('team.common.you')
                : 'You'
              : member.name
            : t
              ? t('team.common.unassigned')
              : '',
          owner: member,
          items: [],
        };
      }

      groupsByOwner[ownerId].items.push(taskItem);
    });

    return Object.values(groupsByOwner).sort((left, right) => {
      if (left.owner?.isCurrentUser) {
        return -1;
      }

      if (right.owner?.isCurrentUser) {
        return 1;
      }

      return right.items.length - left.items.length;
    });
  }

  const groupsByBoard: Record<string, TeamTaskGroup> = {};

  sortedTasks.forEach((taskItem) => {
    const boardKey = taskItem.project.boardId || taskItem.project.name || 'unknown-board';
    if (!groupsByBoard[boardKey]) {
      groupsByBoard[boardKey] = {
        key: boardKey,
        label: taskItem.project.name || (t ? t('team.tasks.noBoard') : ''),
        provider: taskItem.project.appFrom,
        items: [],
      };
    }

    groupsByBoard[boardKey].items.push(taskItem);
  });

  return Object.values(groupsByBoard).sort((left, right) => right.items.length - left.items.length);
};

export const getTeamMembersNotSharingTasks = (
  members: TeamMember[],
  currentUserId: string,
  t?: TeamTFunction
) => {
  return members
    .filter((member) => member.status === 'active')
    .filter((member) => !member.sharedTaskBoardIds?.length)
    .map((member, index) => {
      const name = getTeamMemberDisplayName(member, index, currentUserId, t);
      const initialsSource = member.name?.trim() || name;

      return {
        id: member.teamMemberId || member.userId,
        name,
        initials: getTeamMemberInitials(initialsSource),
        photoUrl: getTeamMemberPhotoUrl(member),
      };
    });
};
